import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Autocomplete,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LockOutlined as LockOutlinedIcon,
  LocalOffer as LocalOfferIcon,
  Save as SaveIcon,
  RefreshRounded as RefreshRoundedIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeAccionProductos } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader } from 'components/financepro';
import { useProductosMaterialContext } from 'context';

/** Misma fila `programa` que la pestaña en MantenimientoProductos (rolesusuarios). */
const PROGRAMA_PROMOCIONES = 'promociones';

/** Mismos estilos de campo que `Motores/informemotores.js` (INPUT_SX). */
const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

const INPUT_MULTILINE_SX = {
  ...INPUT_SX,
  '& .MuiOutlinedInput-root': {
    ...INPUT_SX['& .MuiOutlinedInput-root'],
    height: 'auto',
    minHeight: 56,
    alignItems: 'flex-start',
    py: 1,
  },
};

const outlineBtnSx = {
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  borderRadius: 1.6,
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
};

const primaryBtnSx = {
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  boxShadow: 'none',
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const encabezadoSeccionSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
};

const iconoTituloSx = {
  width: 30,
  height: 30,
  borderRadius: 1.2,
  bgcolor: FP.color.accentBlueSoft,
  color: FP.color.accentBlue,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const formHeaderTitleSx = {
  fontWeight: 700,
  color: FP.color.textPrimary,
  fontSize: '0.98rem',
};

const cellHeadSx = { fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary };

const Promociones = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, roleDetalles, usuarioLog, claveLog } = controller;

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_PROMOCIONES);
  const puedeModificar = puedeAccionProductos(roleDetalles, PROGRAMA_PROMOCIONES, 'modificar');
  const puedeEliminar = puedeAccionProductos(roleDetalles, PROGRAMA_PROMOCIONES, 'borrar');
  
  const [promociones, setPromociones] = useState([]);
  /** Catálogo completo (todas las localidades) para chips en la tabla */
  const [productosCatalogo, setProductosCatalogo] = useState([]);
  /** Productos solo de la localidad elegida en el diálogo */
  const [productosPromoDialog, setProductosPromoDialog] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [localidadPromocion, setLocalidadPromocion] = useState('');
  /** Si ya había localidad guardada, no permitir cambiarla al editar */
  const [bloquearCambioLocalidad, setBloquearCambioLocalidad] = useState(false);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [promocionEditando, setPromocionEditando] = useState(null);
  const [cargando, setCargando] = useState(false);
  
  // Estados del formulario
  const [codigoPromocion, setCodigoPromocion] = useState('');
  const [precioPromocion, setPrecioPromocion] = useState('');
  const [descripcionPromocion, setDescripcionPromocion] = useState('');
  const [productosSeleccionados, setProductosSeleccionados] = useState([]);
  const [productoBusqueda, setProductoBusqueda] = useState('');

  const servidor = process.env.REACT_APP_SERVIDOR || '';

  /** Soporta [{ producto, impuesto, ... }], [{ codigo }], o lista de strings (y variantes legacy). */
  const normalizarCodigoItemPromo = (p) => {
    if (p == null || p === '') return null;
    if (typeof p === 'string') {
      const t = p.trim();
      return t || null;
    }
    if (typeof p === 'object') {
      const v = p.producto ?? p.codigo ?? p.Codigo ?? p.code;
      if (v != null && typeof v === 'string') {
        const t = v.trim();
        return t || null;
      }
      if (v != null && typeof v !== 'object') {
        const t = String(v).trim();
        return t || null;
      }
    }
    return null;
  };

  const parseProductosPromoACodigos = (raw) => {
    if (raw == null) return [];
    let v = raw;
    if (typeof v === 'string') {
      try {
        v = JSON.parse(v);
      } catch {
        return [];
      }
    }
    if (!Array.isArray(v)) return [];
    const codigos = [];
    for (const item of v) {
      const c = normalizarCodigoItemPromo(item);
      if (c) codigos.push(c);
    }
    return codigos;
  };

  const fetchProductosLista = useCallback(
    async (localidadId) => {
      try {
        const extra = {
          tabla_inventario: selectedCompany.anomesfacturacion,
        };
        if (localidadId) {
          extra.localidad = localidadId;
        }
        const response = await fetch(servidor + '/api/productosbackend_listarproductos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            ...extra,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          return data.productos || [];
        }
      } catch (e) {
        console.error('Error al cargar productos:', e);
      }
      return [];
    },
    [selectedCompany, usuarioLog, claveLog, servidor]
  );

  const cargarLocalidades = useCallback(async () => {
    if (!selectedCompany?.db_empresa) return;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerlocalidades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setLocalidades(data.localidades || []);
      }
    } catch (e) {
      console.error('Error al cargar localidades:', e);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor]);

  const cargarPromociones = useCallback(async () => {
    if (!selectedCompany?.db_empresa) return;
    setCargando(true);
    try {
      const response = await fetch(servidor + '/api/productosbackend_listarpromociones', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();

        setPromociones(data.promociones || []);
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al cargar promociones');
      }
    } catch (error) {
      showError('Error de conexión al cargar promociones');
    } finally {
      setCargando(false);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    if (!selectedCompany?.db_empresa || !puedeLeer) return;
    cargarPromociones();
    cargarLocalidades();
  }, [selectedCompany?.db_empresa, puedeLeer, cargarPromociones, cargarLocalidades]);

  useEffect(() => {
    if (!selectedCompany?.db_empresa || !puedeLeer) return;
    (async () => {
      const all = await fetchProductosLista();
      setProductosCatalogo(all);
    })();
  }, [fetchProductosLista, selectedCompany?.db_empresa, puedeLeer]);

  const textoLocalidad = (idLoc) => {
    const s = String(idLoc || '').trim();
    if (!s) return '—';
    const loc = localidades.find((l) => String(l.id) === s);
    return loc?.texto || s;
  };

  const handleAbrirDialogo = async (promocion = null) => {
    if (!puedeModificar) {
      showError('No tiene permiso para crear o modificar promociones.');
      return;
    }
    setProductoBusqueda('');
    if (promocion) {
      setModoEdicion(true);
      setBloquearCambioLocalidad(!!String(promocion.localidad || '').trim());
      setPromocionEditando(promocion);
      setCodigoPromocion(promocion.codigo || '');
      setPrecioPromocion(promocion.precio ? String(promocion.precio) : '');
      setDescripcionPromocion(promocion.descripcion || '');
      const loc = String(promocion.localidad || '').trim();
      setLocalidadPromocion(loc);
      const listaLoc = loc ? await fetchProductosLista(loc) : [];
      setProductosPromoDialog(listaLoc);

      if (promocion.productos) {
        try {
          const codigosPromo = parseProductosPromoACodigos(promocion.productos);
          const productosCompletos = codigosPromo.map((c) => {
            const productoEncontrado = listaLoc.find((p) => String(p.codigo) === String(c));
            return (
              productoEncontrado || {
                codigo: c,
                descripcion: `Producto ${c} (no encontrado en esta localidad)`,
                localidad: loc || undefined,
              }
            );
          });
          setProductosSeleccionados(productosCompletos);
        } catch (e) {
          console.error('Error al parsear productos:', e);
          setProductosSeleccionados([]);
        }
      } else {
        setProductosSeleccionados([]);
      }
    } else {
      setModoEdicion(false);
      setBloquearCambioLocalidad(false);
      setPromocionEditando(null);
      setCodigoPromocion('');
      setPrecioPromocion('');
      setDescripcionPromocion('');
      setProductosSeleccionados([]);
      setLocalidadPromocion('');
      setProductosPromoDialog([]);
    }
    setDialogoAbierto(true);
  };

  const handleCerrarDialogo = () => {
    setDialogoAbierto(false);
    setModoEdicion(false);
    setPromocionEditando(null);
    setCodigoPromocion('');
    setPrecioPromocion('');
    setDescripcionPromocion('');
    setProductosSeleccionados([]);
    setProductoBusqueda('');
    setLocalidadPromocion('');
    setBloquearCambioLocalidad(false);
    setProductosPromoDialog([]);
    (async () => {
      const all = await fetchProductosLista();
      setProductosCatalogo(all);
    })();
  };

  const handleAgregarProducto = (producto) => {
    if (producto && !productosSeleccionados.find(p => p.codigo === producto.codigo)) {
      setProductosSeleccionados([...productosSeleccionados, producto]);
      setProductoBusqueda('');
    }
  };

  const handleEliminarProducto = (codigo) => {
    setProductosSeleccionados(productosSeleccionados.filter(p => p.codigo !== codigo));
  };

  const handleGuardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para guardar promociones.');
      return;
    }
    // Validaciones
    if (!codigoPromocion.trim()) {
      showError('El código de la promoción es requerido');
      return;
    }
    
    if (!precioPromocion || parseFloat(precioPromocion) <= 0) {
      showError('El precio debe ser mayor a 0');
      return;
    }
    
    if (!String(localidadPromocion || '').trim()) {
      showError('Debe seleccionar la localidad de la promoción');
      return;
    }

    if (productosSeleccionados.length === 0) {
      showError('Debe agregar al menos un producto a la promoción');
      return;
    }

    const loc = String(localidadPromocion).trim();
    const invalidos = productosSeleccionados.filter(
      (p) => String(p.localidad || '').trim() !== loc
    );
    if (invalidos.length > 0) {
      showError('Todos los productos deben pertenecer a la localidad seleccionada');
      return;
    }

    try {
      const codigosProductos = productosSeleccionados
        .map((p) => (p?.codigo != null ? String(p.codigo).trim() : ''))
        .filter(Boolean);
      if (codigosProductos.length === 0) {
        showError('No hay códigos de producto válidos en la promoción');
        return;
      }

      const promocionData = {
        codigo: codigoPromocion.trim(),
        precio: parseFloat(precioPromocion),
        descripcion: descripcionPromocion.trim(),
        productos: codigosProductos,
        localidad: loc,
      };

      if (modoEdicion && promocionEditando) {
        promocionData.id = promocionEditando.id;
      }

      const response = await fetch(servidor + '/api/productosbackend_guardarpromocion', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: selectedCompany.anomesfacturacion,
          promocion: promocionData,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Promoción guardada exitosamente');
        handleCerrarDialogo();
        cargarPromociones();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al guardar la promoción');
      }
    } catch (error) {
      showError('Error de conexión al guardar');
    }
  };

  const handleEliminar = async (promocion) => {
    if (!puedeEliminar) {
      showError('No tiene permiso para eliminar promociones.');
      return;
    }
    if (!window.confirm('¿Está seguro de eliminar esta promoción?')) {
      return;
    }

    try {
      const response = await fetch(servidor + '/api/productosbackend_eliminarpromocion', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          promocionId: promocion.id,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Promoción eliminada exitosamente');
        cargarPromociones();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al eliminar la promoción');
      }
    } catch (error) {
      showError('Error de conexión al eliminar');
    }
  };

  const obtenerProductosPromocion = (promocion) => {
    if (!promocion?.productos) return [];
    return parseProductosPromoACodigos(promocion.productos);
  };

  if (!selectedCompany?.db_empresa) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Promociones" subtitle="Seleccione una empresa activa" showQuickActions={false} />
        <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              maxWidth: 520,
              width: '100%',
              ...cardShellSx,
              p: { xs: 3, md: 4 },
              textAlign: 'center',
            }}
          >
            <DescriptionIcon sx={{ fontSize: 48, color: FP.color.textMuted, mb: 2 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 1 }}>
              Sin empresa seleccionada
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}>
              Elija una empresa en la barra superior para administrar promociones por localidad.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Promociones" subtitle="Permisos · Acceso restringido" showQuickActions={false} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3, md: 4 } }}>
          <Card
            elevation={0}
            sx={{
              maxWidth: 560,
              width: '100%',
              ...cardShellSx,
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso de lectura del programa «promociones» en roles (módulo Productos).
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  const subtitleHeader =
    nombreNegocio ? `${nombreNegocio} · Mantenimiento de productos` : 'Mantenimiento de productos';

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Promociones"
        subtitle={subtitleHeader}
        cta={
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => cargarPromociones()}
              disabled={cargando}
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                px: 2,
                height: 38,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleAbrirDialogo()}
              disabled={!puedeModificar}
              sx={primaryBtnSx}
            >
              Nueva promoción
            </Button>
          </Box>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={cardShellSx}>
          <Box sx={encabezadoSeccionSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
              <Box sx={iconoTituloSx}>
                <LocalOfferIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={formHeaderTitleSx}>Listado</Typography>
            </Box>
            <Typography
              sx={{
                color: FP.color.textSecondary,
                fontSize: '0.78rem',
                fontWeight: 600,
                display: { xs: 'none', md: 'block' },
              }}
            >
              Paquetes y precios especiales por localidad
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {cargando ? (
              <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                Cargando…
              </Typography>
            ) : promociones.length === 0 ? (
              <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                No hay promociones configuradas.
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 2,
                  overflow: 'auto',
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: FP.color.surfaceAlt }}>
                      <TableCell>
                        <Typography sx={cellHeadSx}>Código</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={cellHeadSx}>Descripción</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={cellHeadSx}>Localidad</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={cellHeadSx}>Precio</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={cellHeadSx}>Productos</Typography>
                      </TableCell>
                      {(puedeModificar || puedeEliminar) && (
                        <TableCell align="center" className="no-print">
                          <Typography sx={cellHeadSx}>Acciones</Typography>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {promociones.map((promocion) => {
                      const productosPromo = obtenerProductosPromocion(promocion);
                      return (
                        <TableRow key={promocion.id} hover>
                          <TableCell sx={{ fontWeight: 700 }}>{promocion.codigo}</TableCell>
                          <TableCell>{promocion.descripcion || '—'}</TableCell>
                          <TableCell>{textoLocalidad(promocion.localidad)}</TableCell>
                          <TableCell>
                            {promocion.precio ? parseFloat(promocion.precio).toFixed(2) : '0.00'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {productosPromo.length > 0 ? (
                                productosPromo.map((codigo, idx) => {
                                  const producto =
                                    productosCatalogo.find(
                                      (p) =>
                                        p.codigo === codigo &&
                                        String(p.localidad || '').trim() === String(promocion.localidad || '').trim()
                                    ) || productosCatalogo.find((p) => p.codigo === codigo);
                                  return (
                                    <Chip
                                      key={idx}
                                      label={producto ? `${codigo} — ${producto.descripcion}` : codigo}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        borderColor: FP.color.borderStrong,
                                        color: FP.color.textPrimary,
                                        fontWeight: 600,
                                        bgcolor: FP.color.cardBg,
                                      }}
                                    />
                                  );
                                })
                              ) : (
                                <Typography variant="body2" sx={{ color: FP.color.textMuted }}>
                                  Sin productos
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          {(puedeModificar || puedeEliminar) && (
                            <TableCell align="center" className="no-print">
                              {puedeModificar && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleAbrirDialogo(promocion)}
                                  aria-label="Editar promoción"
                                  sx={{
                                    color: FP.color.accentBlue,
                                    '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              {puedeEliminar && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleEliminar(promocion)}
                                  aria-label="Eliminar promoción"
                                  sx={{
                                    color: FP.color.danger,
                                    '&:hover': { bgcolor: FP.color.dangerSoft },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogoAbierto}
        onClose={handleCerrarDialogo}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <LocalOfferIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }}>
              {modoEdicion ? 'Editar promoción' : 'Nueva promoción'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCerrarDialogo}
            size="small"
            aria-label="Cerrar"
            sx={{
              color: FP.color.textSecondary,
              border: `1px solid ${FP.color.border}`,
              borderRadius: 1.2,
              width: 32,
              height: 32,
              '&:hover': { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
            }}
          >
            <ClearIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, overflow: 'visible' }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required sx={INPUT_SX}>
                <InputLabel id="dlg-promo-loc">Localidad (inventario)</InputLabel>
                <Select
                  labelId="dlg-promo-loc"
                  value={localidadPromocion}
                  label="Localidad (inventario)"
                  disabled={modoEdicion && bloquearCambioLocalidad}
                  onChange={async (e) => {
                    const v = e.target.value;
                    setLocalidadPromocion(v);
                    setProductosSeleccionados([]);
                    setProductoBusqueda('');
                    setProductosPromoDialog(v ? await fetchProductosLista(v) : []);
                  }}
                >
                  <MenuItem value="">
                    <em>Seleccione localidad</em>
                  </MenuItem>
                  {localidades.map((loc) => (
                    <MenuItem key={loc.id} value={String(loc.id)}>
                      {loc.texto || loc.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {modoEdicion && bloquearCambioLocalidad && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: FP.color.textSecondary }}>
                  La localidad no se puede cambiar al editar; elimine la promoción y cree una nueva si necesita otro almacén.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Código de promoción"
                value={codigoPromocion}
                onChange={(e) => setCodigoPromocion(e.target.value.toUpperCase())}
                required
                disabled={modoEdicion}
                InputLabelProps={{ shrink: true }}
                helperText={modoEdicion ? 'El código no se puede modificar' : 'Código único de la promoción'}
                sx={INPUT_SX}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Precio"
                type="number"
                value={precioPromocion}
                onChange={(e) => setPrecioPromocion(e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Precio de la promoción"
                sx={INPUT_SX}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={descripcionPromocion}
                onChange={(e) => setDescripcionPromocion(e.target.value)}
                multiline
                rows={2}
                InputLabelProps={{ shrink: true }}
                helperText="Descripción opcional de la promoción"
                sx={INPUT_MULTILINE_SX}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: FP.color.textPrimary }}>
                Productos de la promoción
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={productosPromoDialog.filter(
                    (p) => !productosSeleccionados.find((sp) => sp.codigo === p.codigo)
                  )}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    return `${option.codigo || ''} - ${option.descripcion || ''}`;
                  }}
                  value={null}
                  inputValue={productoBusqueda}
                  onInputChange={(e, newValue) => setProductoBusqueda(newValue)}
                  onChange={(e, newValue) => {
                    if (newValue) {
                      handleAgregarProducto(newValue);
                      setProductoBusqueda('');
                    }
                  }}
                  disabled={!localidadPromocion}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar y agregar producto"
                      InputLabelProps={{ shrink: true }}
                      placeholder={
                        localidadPromocion ? 'Escriba para buscar…' : 'Seleccione primero la localidad…'
                      }
                      sx={INPUT_SX}
                    />
                  )}
                />
              </Box>

              {productosSeleccionados.length > 0 ? (
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  elevation={0}
                  sx={{ borderRadius: 2, borderColor: FP.color.border }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: FP.color.surfaceAlt }}>
                        <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>Código</Typography></TableCell>
                        <TableCell><Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>Descripción</Typography></TableCell>
                        <TableCell align="center" className="no-print"><Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>Acción</Typography></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productosSeleccionados.map((producto) => (
                        <TableRow key={producto.codigo}>
                          <TableCell>{producto.codigo}</TableCell>
                          <TableCell>{producto.descripcion}</TableCell>
                          <TableCell align="center" className="no-print">
                            <IconButton
                              size="small"
                              onClick={() => handleEliminarProducto(producto.codigo)}
                              aria-label="Quitar producto"
                              sx={{
                                color: FP.color.danger,
                                '&:hover': { bgcolor: FP.color.dangerSoft },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No hay productos agregados. Use el buscador para agregar productos.
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${FP.color.border}`, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={handleCerrarDialogo} sx={outlineBtnSx}>
            Cancelar
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleGuardar} sx={primaryBtnSx}>
            {modoEdicion ? 'Modificar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Promociones;
